import { get } from '@/services/api/resources/integrationProperty/actions';
import { create as createAlertMonitor } from '@/services/api/resources/prismatic-alert-monitor/actions';
import {
  create as createIntegrationWebhooks,
  remove as deleteIntegrationWebhooks,
} from '@/services/api/resources/prismatic-webhooks/actions';
import prismaticClient from '@/services/integrations/prismaticClient';
import { ActivatedIntegration, Instance, Integration, IntegrationDetailed, IntegrationID } from '@/services/integrations/types';
import emitter from '@/services/emitter';
import { useTask } from '@/ui/concerns';
import { taskToPromise } from '@/ui/helpers/fpTsHelper';

const sharePointSitesEndpoint = 'https://graph.microsoft.com/v1.0/sites?search=*';

class IntegrationsService {
  public isActivated(integration: Integration): integration is ActivatedIntegration {
    return integration.instances.nodes.length > 0 && integration.instances.nodes[0].configState === 'FULLY_CONFIGURED';
  }

  public isEnabled(integration: Integration) {
    return this.isActivated(integration) && integration.instances.nodes[0].enabled;
  }

  public isPaused(integration: Integration) {
    return this.isActivated(integration) && !integration.instances.nodes[0].enabled;
  }

  public hasNewVersion(integration: ActivatedIntegration & IntegrationDetailed): boolean {
    return integration.latestVersion.versionNumber > integration.instances.nodes[0].deployedVersion;
  }

  public getInstanceId(integration: ActivatedIntegration) {
    return integration.instances.nodes[0].id;
  }

  public async upgradeInstance(integration: ActivatedIntegration & IntegrationDetailed): Promise<IntegrationID> {
    const integrationId = integration.latestVersion.id;
    await prismaticClient.updateInstanceVersion(this.getInstanceId(integration), integrationId);
    await prismaticClient.deployInstance(this.getInstanceId(integration));

    return integrationId;
  }

  public async destroyAllInstances(integration: Integration, excludeId?: string) {
    const promises = integration.instances.nodes
      .filter(instance => instance.id !== excludeId)
      .map(instance => {
        return prismaticClient.deleteInstance(instance.id).then(() => {
          this.onInstanceDeleted(instance);
        });
      });

    await Promise.all(promises);

    integration.instances = {
      nodes: [],
      totalCount: 0,
    };

    emitter.emit('integrationUpdated', integration);
  }

  public setIntegrationStatus(integration: ActivatedIntegration, enabled: boolean) {
    integration.instances.nodes[0].enabled = enabled;

    emitter.emit('integrationUpdated', integration);
  }

  public async resolveAvatarUrl(integration: Integration): Promise<string|null> {
    if (!integration.avatarUrl) {
      return null;
    }

    if (!integration.avatarUrlResolved) {
      integration.avatarUrlResolved = await prismaticClient.resolveAvatarUrl(integration.avatarUrl);
    }

    return integration.avatarUrlResolved;
  }

  public async getProperties(integrationId: IntegrationID) {
    return taskToPromise(get, integrationId);
  }

  public onInstanceCreated(instance: Instance) {
    useTask(
      () => createAlertMonitor(instance.id),
    ).run();
  }

  public onInstanceDeployed(instance: Instance) {
    useTask(
      () => createIntegrationWebhooks(instance.id),
    ).run();
  }

  public onInstanceDeleted(instance: Instance) {
    useTask(
      () => deleteIntegrationWebhooks(instance.id),
    ).run();
  }

  public async getSharePointSites(accessToken: string): Promise<Array<{ id: string; name: string }>> {
    const response = await fetch(sharePointSitesEndpoint, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    const json = await response.json();

    return json
      .value
      ?.map((v: { id: string; displayName: string }) => ({ id: v.id, name: v.displayName }));
  }
}

export const integrationsService = new IntegrationsService();
